import { defineStore } from 'pinia'
import { ref } from 'vue'

import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'

import type { ProjectPermissionType, Role } from '@/stores/type/authorization.type'
import { useBaseStore } from '@haskoning/vue-basestore'

const { api } = useApi()

const baseUrl = API.ROLES.INDEX

export const useAuthorizationStore = defineStore('authorization', () => {
  const baseStore = useBaseStore<Role>({ service: baseUrl, api })

  // Initialize arcgisToken from localStorage if available
  const arcgisToken = ref<string>(localStorage.getItem('arcgisToken') || '')

  const currentRoles = ref<Role[]>([])

  function setCurrentRole(roleIds: number[]) {
    roleIds.forEach((roleId: number) => {
      const role = getCurrentRole(roleId)
      const isExist = currentRoles.value.find((item) => item.id === roleId)
      if (role && !isExist) {
        currentRoles.value.push(role)
      }
    })
  }

  function getCurrentRole(roleId: number) {
    return baseStore.items.value.find((item) => item.id === roleId)
  }

  function hasServicePermission({
    authSource,
    allowed
  }: {
    authSource: string
    allowed: string[]
  }) {
    const authRoles = currentRoles.value.find((role) => role.auth_source === authSource)
    return authRoles
      ? authRoles.permissions.some((permission) => allowed.includes(permission))
      : false
  }

  function resetState() {
    baseStore.items.value = []
    currentRoles.value = []
    setCurrentRole([])
  }

  function getAuthPermissionItems(roleItem: { id: number; role: number }, permissions: string[]) {
    const authPermission: string[] = []
    permissions.forEach((permissionItem) => {
      const authRoles = getCurrentRole(roleItem.role)
      if (!authRoles) return
      const hasPermission = authRoles.permissions.includes(permissionItem)
      if (hasPermission) {
        const p = permissionItem.split('_')[0]
        authPermission.push(p)
      }
    })
    return authPermission
  }

  async function getServicePermissionByRole(
    roleItems: { id: number; role: number }[],
    permissions: string[]
  ): Promise<ProjectPermissionType[]> {
    resetState()
    await baseStore.fetchItems()
    const authPermission: ProjectPermissionType[] = []
    roleItems.forEach((roleItem) => {
      const authPermissionItems = getAuthPermissionItems(roleItem, permissions)
      authPermission.push({
        ...roleItem,
        permissions: authPermissionItems
      })
    })
    return authPermission
  }

  // Add setToken and clearToken for ArcGIS token persistence
  function setToken(token: string) {
    arcgisToken.value = token
    localStorage.setItem('arcgisToken', token)
  }

  function clearToken() {
    arcgisToken.value = ''
    localStorage.removeItem('arcgisToken')
    localStorage.removeItem('sessionToken')
  }

  return {
    ...baseStore,
    currentRoles,
    setCurrentRole,
    getCurrentRole,
    hasServicePermission,
    resetState,
    getServicePermissionByRole,
    arcgisToken,
    setToken,
    clearToken
  }
})
